import { Button } from "../../shadcn/ui/Button";
import { CheckCircle2, Eye, Heart } from "lucide-react";
import { getProductDetailShareUrl } from "../../lib/productRoutes";

export interface BizcoverProductListCardProps {
  id: string;
  slug?: string;
  title: string;
  price: string;
  unitPrice?: string;
  subtitle?: string;
  subtitleVerified?: boolean;
  imageUrl?: string;
  onViewProduct?: (id: string, slug?: string) => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  onToggleFavorite?: (id: string, isFavorited: boolean) => void;
  showFavorite?: boolean;
}

export const BizcoverProductListCard = ({
  id,
  slug,
  title,
  price,
  unitPrice,
  subtitle,
  subtitleVerified = false,
  imageUrl,
  onViewProduct,
  isFavorited = false,
  isFavoriteLoading = false,
  onToggleFavorite,
  showFavorite = false,
}: BizcoverProductListCardProps) => {
  const handleView = () => {
    if (onViewProduct) {
      onViewProduct(id, slug);
      return;
    }
    const url = getProductDetailShareUrl({ id, slug });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      role="group"
      tabIndex={0}
      aria-label={`${title}. View product.`}
      className="group min-w-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-2.5 transition-shadow hover:shadow-md outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2"
      onClick={handleView}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("button, a")) return;
        e.preventDefault();
        handleView();
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
            No Image Preview
          </div>
        )}
      </div>

      <div className="mt-3">
        <h3 className="line-clamp-2 min-h-[2.5rem] break-words text-sm font-semibold text-gray-900">
          {title}
        </h3>

        {subtitle ? (
          <div className="mt-2 flex min-w-0 items-center gap-1 text-xs text-gray-500">
            <span className="block truncate">{subtitle}</span>
            {subtitleVerified ? (
              <span className="inline-flex items-center text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2">
          <span className="text-base font-bold tabular-nums text-[#F97316]">{price}</span>
          {unitPrice ? (
            <p className="mt-0.5 text-xs text-gray-600">{unitPrice}</p>
          ) : null}
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          {showFavorite && onToggleFavorite ? (
            <button
              type="button"
              disabled={isFavoriteLoading}
              onClick={(ev) => {
                ev.stopPropagation();
                onToggleFavorite(id, !isFavorited);
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50"
              aria-label={isFavorited ? "Remove from favorites" : "Save product"}
            >
              <Heart
                className={`h-4 w-4 ${
                  isFavorited ? "fill-[#F97316] text-[#F97316]" : ""
                }`}
              />
            </button>
          ) : null}

          <Button
            size="sm"
            className="h-7 min-w-0 flex-1 whitespace-nowrap rounded-full bg-[#F97316] px-2 text-white hover:bg-[#ea6820]"
            onClick={(ev) => {
              ev.stopPropagation();
              handleView();
            }}
          >
            <Eye className="mr-1 h-3.5 w-3.5" />
            View Product
          </Button>
        </div>
      </div>
    </article>
  );
};
